import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Box, Typography, Paper, Alert, Button, Stack, TextField,
  Autocomplete, CircularProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Tooltip
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import { dcApi } from '../services/dc.api'
import { Distributor, MasterMedicine } from '../types/dc.types'

interface ManualItem {
  key: number
  medicine: MasterMedicine | null
  product_name: string
  pack_size: string
  batch_no: string
  expiry_date: string
  quantity: number
  free_quantity: number
  rate: number
  mrp: number
  discount_pct: number
  gst_pct: number
}

const emptyItem = (key: number): ManualItem => ({
  key,
  medicine: null,
  product_name: '',
  pack_size: '',
  batch_no: '',
  expiry_date: '',
  quantity: 0,
  free_quantity: 0,
  rate: 0,
  mrp: 0,
  discount_pct: 0,
  gst_pct: 0
})

function calcLineTotal(item: ManualItem): number {
  const discountedRate = item.rate * (1 - item.discount_pct / 100)
  const base = discountedRate * item.quantity
  return Math.round(base * (1 + item.gst_pct / 100) * 100) / 100
}

export default function DCManualPurchaseEntryPage() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [selectedDistributor, setSelectedDistributor] = useState<Distributor | null>(null)
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [invoiceDate, setInvoiceDate] = useState('')
  const [items, setItems] = useState<ManualItem[]>([emptyItem(1)])
  const [nextKey, setNextKey] = useState(2)

  const { data: distributors = [], isLoading: distributorsLoading } = useQuery({
    queryKey: ['distributors'],
    queryFn: () => dcApi.getDistributors()
  })

  const saveMutation = useMutation({
    mutationFn: () => {
      const validItems = items.filter(i => i.product_name && i.quantity > 0)
      if (validItems.length === 0) throw new Error('Add at least one item with a product name and quantity')
      if (!selectedDistributor) throw new Error('Select a distributor')

      return dcApi.createManualPurchase({
        distributor_id: selectedDistributor.id,
        distributor_name: selectedDistributor.distributor_name,
        invoice_number: invoiceNumber || undefined,
        invoice_date: invoiceDate || undefined,
        items: validItems.map(i => ({
          product_name: i.product_name,
          pack_size: i.pack_size || undefined,
          batch_no: i.batch_no || undefined,
          expiry_date: i.expiry_date || undefined,
          quantity: i.quantity,
          free_quantity: i.free_quantity || undefined,
          rate: i.rate,
          mrp: i.mrp || undefined,
          discount_pct: i.discount_pct || undefined,
          cgst_pct: i.gst_pct ? i.gst_pct / 2 : undefined,
          sgst_pct: i.gst_pct ? i.gst_pct / 2 : undefined,
          medicine_id: i.medicine?.id,
          manufacturer_name: i.medicine?.manufacturer_name || undefined
        }))
      })
    },
    onSuccess: (data) => {
      navigate(`/purchases/${data.purchaseId}`)
    },
    onError: (err: Error) => {
      setError(err.message)
    }
  })

  const addItem = useCallback(() => {
    setItems(prev => [...prev, emptyItem(nextKey)])
    setNextKey(prev => prev + 1)
  }, [nextKey])

  const removeItem = useCallback((key: number) => {
    setItems(prev => prev.length > 1 ? prev.filter(i => i.key !== key) : prev)
  }, [])

  const updateItem = useCallback((key: number, field: keyof ManualItem, value: unknown) => {
    setItems(prev => prev.map(i => i.key === key ? { ...i, [field]: value } : i))
  }, [])

  const handleMedicineSelect = useCallback((key: number, medicine: MasterMedicine | null) => {
    setItems(prev => prev.map(i => {
      if (i.key !== key) return i
      if (!medicine) return { ...i, medicine: null }
      return {
        ...i,
        medicine,
        product_name: medicine.medicine_name,
        pack_size: medicine.pack_size || '',
        mrp: medicine.mrp || 0,
        gst_pct: medicine.gst_percentage || 0
      }
    }))
  }, [])

  const grandTotal = items.reduce((sum, i) => sum + calcLineTotal(i), 0)

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/purchases')}>
          Back
        </Button>
        <Typography variant="h4">Manual Purchase Entry</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Invoice Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Invoice Details
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Autocomplete
            value={selectedDistributor}
            onChange={(_, v) => setSelectedDistributor(v)}
            options={distributors}
            getOptionLabel={(o) => o.distributor_name}
            loading={distributorsLoading}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Distributor *"
                placeholder="Search distributors..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {distributorsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
            sx={{ minWidth: 300 }}
          />
          <TextField
            label="Invoice #"
            value={invoiceNumber}
            onChange={e => setInvoiceNumber(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <TextField
            label="Invoice Date"
            type="date"
            value={invoiceDate}
            onChange={e => setInvoiceDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180 }}
          />
        </Stack>
      </Paper>

      {/* Items Table */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Items ({items.length})
          </Typography>
          <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={addItem}>
            Add Item
          </Button>
        </Box>
        <TableContainer>
          <Table size="small" sx={{ tableLayout: 'fixed', '& .MuiTableCell-root': { px: 0.5, py: 0.5 }, '& .MuiInputBase-input': { px: 1, py: 0.75, fontSize: '0.85rem' } }}>
            <colgroup>
              <col style={{ width: '22%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '6%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '4%' }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell>Medicine</TableCell>
                <TableCell>Batch</TableCell>
                <TableCell>Expiry</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Free</TableCell>
                <TableCell align="right">Rate</TableCell>
                <TableCell align="right">MRP</TableCell>
                <TableCell align="right">Disc%</TableCell>
                <TableCell align="right">GST%</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(item => (
                <ItemRow
                  key={item.key}
                  item={item}
                  onUpdate={updateItem}
                  onRemove={removeItem}
                  onMedicineSelect={handleMedicineSelect}
                  canRemove={items.length > 1}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Footer with total */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button startIcon={<AddIcon />} size="small" onClick={addItem}>
            Add Item
          </Button>
          <Typography variant="h6">
            Total: Rs. {grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </Typography>
        </Box>
      </Paper>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button variant="outlined" onClick={() => navigate('/purchases')}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={saveMutation.isPending ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending || !selectedDistributor || items.every(i => !i.product_name || i.quantity <= 0)}
        >
          {saveMutation.isPending ? 'Saving...' : 'Save as Draft'}
        </Button>
      </Box>
    </Box>
  )
}

function ItemRow({
  item,
  onUpdate,
  onRemove,
  onMedicineSelect,
  canRemove
}: {
  item: ManualItem
  onUpdate: (key: number, field: keyof ManualItem, value: unknown) => void
  onRemove: (key: number) => void
  onMedicineSelect: (key: number, medicine: MasterMedicine | null) => void
  canRemove: boolean
}) {
  const [searchQuery, setSearchQuery] = useState('')

  const { data: medicines = [], isLoading } = useQuery({
    queryKey: ['searchMedicines', searchQuery],
    queryFn: () => dcApi.searchMedicines(searchQuery, 15),
    enabled: searchQuery.length >= 2,
    staleTime: 30000
  })

  return (
    <TableRow>
      <TableCell>
        <Autocomplete<MasterMedicine, false, false, true>
          value={item.medicine}
          onChange={(_, v) => onMedicineSelect(item.key, typeof v === 'string' ? null : v)}
          onInputChange={(_, v, reason) => {
            if (reason === 'input') {
              setSearchQuery(v)
              onUpdate(item.key, 'product_name', v)
            }
          }}
          options={medicines}
          getOptionLabel={(o) => typeof o === 'string' ? o : `${o.medicine_name} ${o.pack_size || ''}`}
          loading={isLoading}
          filterOptions={(x) => x}
          freeSolo
          isOptionEqualToValue={(o, v) => o.id === v.id}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search medicine..."
              size="small"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box>
                <Typography variant="body2" fontWeight={600}>{option.medicine_name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.pack_size} | {option.manufacturer_name}
                </Typography>
              </Box>
            </li>
          )}
          size="small"
        />
      </TableCell>
      <TableCell>
        <TextField
          value={item.batch_no}
          onChange={e => onUpdate(item.key, 'batch_no', e.target.value)}
          size="small"
          variant="outlined"
          fullWidth
        />
      </TableCell>
      <TableCell>
        <TextField
          type="date"
          value={item.expiry_date}
          onChange={e => onUpdate(item.key, 'expiry_date', e.target.value)}
          size="small"
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
      </TableCell>
      <TableCell>
        <TextField
          type="number"
          value={item.quantity || ''}
          onChange={e => onUpdate(item.key, 'quantity', parseInt(e.target.value) || 0)}
          size="small"
          variant="outlined"
          inputProps={{ min: 0, style: { textAlign: 'right' } }}
          fullWidth
        />
      </TableCell>
      <TableCell>
        <TextField
          type="number"
          value={item.free_quantity || ''}
          onChange={e => onUpdate(item.key, 'free_quantity', parseInt(e.target.value) || 0)}
          size="small"
          variant="outlined"
          inputProps={{ min: 0, style: { textAlign: 'right' } }}
          fullWidth
        />
      </TableCell>
      <TableCell>
        <TextField
          type="number"
          value={item.rate || ''}
          onChange={e => onUpdate(item.key, 'rate', parseFloat(e.target.value) || 0)}
          size="small"
          variant="outlined"
          inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
          fullWidth
        />
      </TableCell>
      <TableCell>
        <TextField
          type="number"
          value={item.mrp || ''}
          onChange={e => onUpdate(item.key, 'mrp', parseFloat(e.target.value) || 0)}
          size="small"
          variant="outlined"
          inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
          fullWidth
        />
      </TableCell>
      <TableCell>
        <TextField
          type="number"
          value={item.discount_pct || ''}
          onChange={e => onUpdate(item.key, 'discount_pct', parseFloat(e.target.value) || 0)}
          size="small"
          variant="outlined"
          inputProps={{ min: 0, max: 100, step: 0.1, style: { textAlign: 'right' } }}
          fullWidth
        />
      </TableCell>
      <TableCell>
        <TextField
          type="number"
          value={item.gst_pct || ''}
          onChange={e => onUpdate(item.key, 'gst_pct', parseFloat(e.target.value) || 0)}
          size="small"
          variant="outlined"
          inputProps={{ min: 0, max: 28, step: 0.1, style: { textAlign: 'right' } }}
          fullWidth
        />
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" fontWeight={600} noWrap>
          {calcLineTotal(item).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
        </Typography>
      </TableCell>
      <TableCell>
        <Tooltip title="Remove item">
          <span>
            <IconButton
              size="small"
              onClick={() => onRemove(item.key)}
              disabled={!canRemove}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  )
}
